import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, Leaf, Users, TrendingUp, ShieldCheck, Waves, Radar, Cpu, Coins } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, Overline, EASE } from "@/components/motion";
import StoryFilm from "@/components/StoryFilm";
import { IMAGES } from "@/i18n";

const flowIcons = [Waves, Radar, Cpu, Coins];
const chainIcons = [Leaf, Users, TrendingUp, ShieldCheck];

function StoryReel() {
  const { t } = useLang();
  const r = t.impact.reel;
  const imgs = [IMAGES.microalgae_pond_aerial, IMAGES.coastal_buoy_sensors, null, IMAGES.hero_aquaculture];
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % r.slides.length), 4600);
    return () => clearInterval(id);
  }, [r.slides.length]);

  return (
    <section className="border-b border-lined bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>{r.overline}</Overline>
          <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
            {r.title}
          </h2>
        </Reveal>
        <div data-testid="carbon-story-reel" className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          <div className="flex flex-col gap-2 lg:col-span-2">
            {r.slides.map((s, i) => (
              <button
                key={i}
                data-testid={`reel-step-${i}`}
                onClick={() => setActive(i)}
                className={`relative overflow-hidden rounded-2xl border p-5 text-left transition-colors duration-500 ${
                  active === i ? "border-teal/30 bg-bone" : "border-transparent hover:bg-bone/70"
                }`}
              >
                <p
                  className={`font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-500 ${
                    active === i ? "text-teal" : "text-ink/40"
                  }`}
                >
                  {s.k}
                </p>
                <p
                  className={`mt-1 font-headline text-base font-bold transition-colors duration-500 ${
                    active === i ? "text-deep" : "text-ink/50"
                  }`}
                >
                  {s.title}
                </p>
                {active === i && (
                  <motion.span
                    key={`progress-${active}`}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 4.6, ease: "linear" }}
                    className="absolute bottom-0 left-0 h-0.5 bg-aqua"
                  />
                )}
              </button>
            ))}
          </div>
          <div className="relative min-h-[22rem] overflow-hidden rounded-3xl lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                data-testid={`reel-slide-${active}`}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="absolute inset-0"
              >
                {imgs[active] ? (
                  <>
                    <img
                      src={imgs[active]}
                      alt={r.slides[active].title}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" aria-hidden="true" />
                  </>
                ) : (
                  <div className="grid-pattern absolute inset-0 flex items-center justify-center bg-gradient-to-br from-sky-950 to-slate-950">
                    <motion.span
                      animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                      className="absolute h-44 w-44 rounded-full border border-aqua/50"
                      aria-hidden="true"
                    />
                    <motion.span
                      animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 1.2 }}
                      className="absolute h-44 w-44 rounded-full border border-aqua/50"
                      aria-hidden="true"
                    />
                    <span className="rounded-2xl bg-aqua/15 p-5">
                      <Cpu className="h-10 w-10 text-aqua" />
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-aqua">
                    {r.slides[active].k}
                  </p>
                  <h3 className="mt-2 font-headline text-2xl font-bold text-bone">{r.slides[active].title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-bone/80">{r.slides[active].desc}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function LiveTelemetry() {
  const { t } = useLang();
  const lines = t.hero?.telemetryLines || [];
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    if (!lines.length) return;
    const id = setInterval(() => setVisible((v) => (v % lines.length) + 1), 2000);
    return () => clearInterval(id);
  }, [lines.length]);
  return (
    <div data-testid="live-telemetry-simulation-card" className="rounded-3xl border border-lined bg-white/80 p-7 shadow-lg shadow-sky-900/5 backdrop-blur-md sm:p-9">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-teal">
          {t.impact.telemetry.title}
        </p>
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aqua opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-aqua" />
        </span>
      </div>
      <div className="mt-6 flex h-48 flex-col justify-end gap-2.5 overflow-hidden font-mono text-[11px] sm:text-xs">
        {lines.slice(0, visible).slice(-6).map((line, i, arr) => (
          <p key={`${visible}-${i}`} className={`telemetry-line ${i === arr.length - 1 ? "text-ink" : "text-ink/35"}`}>
            <span className="mr-2 text-aqua">▸</span>
            {line}
          </p>
        ))}
      </div>
      <p className="mt-5 border-t border-lined pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
        {t.impact.telemetry.note}
      </p>
    </div>
  );
}

export default function Impact() {
  const { t } = useLang();
  const o = t.impact.oracle;
  return (
    <>
      <section className="emerald-mesh grain-overlay relative overflow-hidden pb-20 pt-20 sm:pb-28 sm:pt-28">
        <div className="grid-pattern absolute inset-0" aria-hidden="true" />
        <div className="hero-glow absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t.impact.hero.overline}</Overline>
            <h1 data-testid="impact-hero-title" className="mt-4 max-w-3xl font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-ink sm:text-5xl">
              {t.impact.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
              {t.impact.hero.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <StoryReel />
      <StoryFilm />

      <section className="bg-bone py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{o.overline}</Overline>
            <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
              {o.title}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-ink/65">{o.desc}</p>
          </Reveal>

          <div data-testid="oracle-architecture-diagram" className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-0">
            {o.flow.map((step, i) => {
              const Icon = flowIcons[i];
              return (
                <Reveal key={i} delay={0.08 * i} className="relative">
                  <div className={`flex h-full flex-col rounded-3xl border p-7 ${i === 3 ? "border-teal/30 bg-deep text-bone" : "border-lined bg-white"}`}>
                    <span className={`inline-flex w-fit rounded-xl p-3 ${i === 3 ? "bg-aqua/15 text-aqua" : "bg-teal/10 text-teal"}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <p className={`mt-5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] ${i === 3 ? "text-aqua" : "text-teal"}`}>
                      0{i + 1}
                    </p>
                    <h3 className="mt-2 font-headline text-lg font-bold">{step}</h3>
                    <p className={`mt-2 text-sm leading-relaxed ${i === 3 ? "text-bone/70" : "text-ink/60"}`}>
                      {o.flowDescs[i]}
                    </p>
                  </div>
                  {i < o.flow.length - 1 && (
                    <ArrowDown className="mx-auto my-2 h-5 w-5 text-teal/40 lg:absolute lg:-right-3.5 lg:top-1/2 lg:my-0 lg:-translate-y-1/2 lg:-rotate-90 lg:text-teal" aria-hidden="true" />
                  )}
                </Reveal>
              );
            })}
          </div>

          <div className="mt-10 grid grid-cols-1 items-stretch gap-8 lg:grid-cols-2">
            <Reveal delay={0.05} className="h-full">
              <LiveTelemetry />
            </Reveal>
            <Reveal delay={0.15} className="h-full">
              <div className="clipped-frame relative h-full min-h-[20rem] overflow-hidden rounded-3xl">
                <img
                  src={IMAGES.microalgae_pond_aerial}
                  alt={t.impact.hero.overline}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" aria-hidden="true" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-bone pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t.impact.chain.overline}</Overline>
            <h2 className="mt-4 max-w-2xl font-headline text-2xl font-bold tracking-tight text-deep sm:text-3xl lg:text-4xl">
              {t.impact.chain.title}
            </h2>
          </Reveal>
          <div data-testid="impact-value-chain-grid" className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {t.impact.chain.items.map((item, i) => {
              const Icon = chainIcons[i];
              return (
                <Reveal key={i} delay={0.07 * i} className="h-full">
                  <div className="card-glow h-full rounded-3xl border border-lined bg-white p-8 transition-shadow duration-500 hover:shadow-xl hover:shadow-teal/10">
                    <span className="inline-flex rounded-xl bg-teal/10 p-3 text-teal">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-6 font-headline text-lg font-bold text-deep">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
